import { Component, ElementRef, OnInit } from '@angular/core';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { RenderEvent } from 'render/render-events/render-events.model';
import { RenderEventsService } from 'render/render-events/render-events.service';
import { IdtSharedRenderService } from 'render/render-shared/idt-shared-render.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { filter, map, Observable, takeUntil } from 'rxjs';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';
import { toNumber } from 'shared/utils/data-utils';

import { PieceConcludedEventsSettings } from 'extensions/st/piece-concluded-events/front/models/piece-concluded-events.models';


@Component({
  selector: 'piece-concluded-events-render',
  templateUrl: './piece-concluded-events-render.component.html',
  styleUrls: ['./piece-concluded-events-render.component.scss']
})
export class PieceConcludedEventsRenderComponent
  extends BaseRenderComponent<PieceConcludedEventsSettings>
  implements OnInit {

  public orientation$!: Observable<string>;
  public languageTag: string = '';


  constructor(
    private readonly idtSharedRenderService: IdtSharedRenderService,
    private readonly renderConfigService: RenderConfigurationService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly renderEventsService: RenderEventsService,
    private readonly contextService: ContextService,
    private readonly element: ElementRef
  ) {
    super();
    this.orientation$ = this.idtSharedRenderService.screenOrientation$;
  }

  public ngOnInit(): void {
    this.contextService.getContext$().pipe(
      map((context: EcommerceContext) => ({
        lang: this.normalizeLanguageTag(
          context?.store?.supportedLanguages?.find((l) => l.code === this.lang)?.languageTag
        )
      })),
      filter(({ lang }) => !!lang),
      takeUntil(this.clearSubscriptions$)
    ).subscribe((context) => {
      this.languageTag = context.lang!;
    });
  }

  public shouldDisplayComponent(): boolean {
    const benefitId = this.settings?.general?.benefitId;
    if (!benefitId) {
      return true;
    }

    const storedBenefitId = sessionStorage.getItem('benefit_id');
    return storedBenefitId !== benefitId;
  }

  public getPortraitImageSrc(): LazyDataInterface {
    const assetsUrl = this.renderConfigService.config.assetsUrl;
    const image = this.settings.general.portraitImage?.image;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigService.config.amandaImageVideoManagerEnabled
    };

    akamaiOptions.width = this.assetsSharedService.getWidth(this.element);

    if (!image || !assetsUrl) {
      return {};
    }

    return {
      src: this.assetsSharedService.getAssetUrl(image, assetsUrl, akamaiOptions),
      height: toNumber(this.settings.general.portraitImage?.height),
      width: toNumber(this.settings.general.portraitImage?.width)
    };
  }

  public getLandscapeImageSrc(): LazyDataInterface {
    const assetsUrl = this.renderConfigService.config.assetsUrl;
    const image = this.settings.general.landscapeImage?.image;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigService.config.amandaImageVideoManagerEnabled
    };

    akamaiOptions.width = this.assetsSharedService.getWidth(this.element);

    if (!image || !assetsUrl) {
      return {};
    }

    return {
      src: this.assetsSharedService.getAssetUrl(image, assetsUrl, akamaiOptions),
      width: toNumber(this.settings.general.landscapeImage?.width),
      height: toNumber(this.settings.general.landscapeImage?.height)
    };
  }

  public onGalleryClick(event: Event): void {
    event.preventDefault();

    const renderEvent = {
      event,
      extra: {
        src: this.settings.general?.galleryLink,
        identifier: 'gallery-link',
        type: 'external',
        title: this.settings.general?.galleryCtaText || ''
      }
    } as RenderEvent;
    this.renderEventsService.events.next(renderEvent);
  }

  private normalizeLanguageTag(languageTag?: string): string {
    return languageTag?.replace(/_/g, '-') ?? '';
  }

}
